import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './one-modal-to-rule-them-all.css';

/**
 * One Modal To Rule Them All
 * 
 * A comprehensive, accessible, and flexible modal component that can handle:
 * - Text display with copyable content
 * - Image galleries
 * - Forms and inputs
 * - Custom content
 * - Different sizes and styles
 * 
 * Features:
 * - Full keyboard navigation support
 * - Screen reader accessible
 * - Click outside to close
 * - ESC key to close
 * - Focus management
 * - Responsive design
 * - Dark theme consistent with app
 */
const Modal = ({
  isOpen,
  onClose,
  title = '',
  children,
  size = 'medium', // 'small', 'medium', 'large', 'full'
  type = 'default', // 'default', 'text', 'gallery', 'form', 'custom'
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className = '',
  contentClassName = '',
  overlayClassName = '',
  ...props
}) => {
  const modalRef = useRef(null);
  const overlayRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Handle focus management
  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement;
      
      // Focus the modal
      if (modalRef.current) {
        modalRef.current.focus();
      }
    } else {
      // Restore focus when modal closes
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (event) => {
    if (closeOnOverlayClick && event.target === overlayRef.current) {
      onClose();
    }
  };

  const handleKeyDown = (event) => {
    // Trap focus within modal
    if (event.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    }
  };

  return (
    <div
      ref={overlayRef}
      className={`modal-overlay ${overlayClassName}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      aria-describedby={title ? 'modal-content' : undefined}
    >
      <div
        ref={modalRef}
        className={`modal ${size} ${type} ${className}`}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="modal-header">
            {title && (
              <h2 id="modal-title" className="modal-title">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                className="modal-close-btn"
                onClick={onClose}
                aria-label="Close modal"
                type="button"
              >
                ×
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div id="modal-content" className={`modal-content ${contentClassName}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

// PropTypes for type checking
Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'full']),
  type: PropTypes.oneOf(['default', 'text', 'gallery', 'form', 'custom']),
  showCloseButton: PropTypes.bool,
  closeOnOverlayClick: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  overlayClassName: PropTypes.string,
};

export default Modal;

/**
 * Specialized Modal Components
 * These provide convenient wrappers for common use cases
 */

// Text Modal - for displaying copyable text content
export const TextModal = ({ 
  isOpen, 
  onClose, 
  title = 'Information', 
  content, 
  size = 'large',
  ...props 
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title={title}
    size={size}
    type="text"
    {...props}
  >
    <pre className="modal-text-content">{content}</pre>
  </Modal>
);

TextModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  content: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'full']),
};

// Gallery Modal - for displaying image galleries
export const GalleryModal = ({ 
  isOpen, 
  onClose, 
  title = 'Gallery', 
  images = [], 
  size = 'large',
  ...props 
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title={title}
    size={size}
    type="gallery"
    {...props}
  >
    <div className="modal-gallery">
      {images.length > 0 ? (
        <div className="gallery-grid">
          {images.map((image, index) => (
            <div key={index} className="gallery-item">
              <img 
                src={image.url || image.src} 
                alt={image.alt || `Image ${index + 1}`}
                className="gallery-image"
              />
              {image.caption && (
                <div className="gallery-caption">{image.caption}</div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="no-images">No images to display</div>
      )}
    </div>
  </Modal>
);

GalleryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    src: PropTypes.string,
    alt: PropTypes.string,
    caption: PropTypes.string,
  })),
  size: PropTypes.oneOf(['small', 'medium', 'large', 'full']),
};

// Form Modal - for forms and inputs
export const FormModal = ({ 
  isOpen, 
  onClose, 
  title = 'Form', 
  children, 
  onSubmit,
  submitText = 'Submit',
  cancelText = 'Cancel',
  size = 'medium',
  ...props 
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title={title}
    size={size}
    type="form"
    {...props}
  >
    <form onSubmit={onSubmit} className="modal-form">
      <div className="form-content">
        {children}
      </div>
      <div className="form-actions">
        <button type="button" onClick={onClose} className="btn btn-secondary">
          {cancelText}
        </button>
        <button type="submit" className="btn btn-primary">
          {submitText}
        </button>
      </div>
    </form>
  </Modal>
);

FormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node,
  onSubmit: PropTypes.func,
  submitText: PropTypes.string,
  cancelText: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'full']),
}; 